import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import environment from '@environments/environment';
import { AuthService } from '@auth/auth.service';
import { getFileNameTimeStamp } from '@shared/services/utils.service';
import { saveAs } from '@shared/utils/download.util';
import {
  createDeleteFileWorker,
  createZipFileWorker,
} from './service-worker.util';

interface DeleteFiles {
  id: string;
  entityId: string;
  entityType: string;
}

interface GridFile {
  id: string;
  title: string;
  url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly _http = inject(HttpClient);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _authService = inject(AuthService);

  private zipWorker?: Worker;
  private deleteWorker?: Worker;
  constructor() {
    this.zipWorker = createZipFileWorker() as Worker;
    this.deleteWorker = createDeleteFileWorker() as Worker;
  }

  downloadFile(id: string) {
    const token = this._authService.getToken();
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
      observe: 'response' as 'body',
      headers: new HttpHeaders({
        DocumentDownload: '',
        Authorization: `Bearer ${token}`,
      }),
    };

    return this._http
      .get(`${environment.irisApi}/document/${id}/`, httpOptions)
      .pipe(catchError((error) => of(error)));
  }

  async downloadFiles(
    files: GridFile[],
    fileCategory?: string,
    fileId?: string,
  ) {
    if (files.length === 1) {
      this.showSnackbar('Downloading File...', 'snackbar-loading');
      this.downloadFile(files[0].id).subscribe((response) => {
        if (response.ok) {
          this.saveFile(response, files[0].title);
          this.showSnackbar('Download complete', 'snackbar-success');
        } else {
          this.showSnackbar(
            'Failed to download file. Please try to re-download the failed file. If you continue to have issues, please contact WCG Client Care as listed under Contact Us.',
            'snackbar-error',
          );
        }
      });
    } else {
      this.downloadAsZip(files, fileCategory, fileId);
    }
  }

  async downloadAsZip(
    files: GridFile[],
    fileCategory?: string,
    fileId?: string,
  ) {
    this.showSnackbar('Downloading ZIP File...', 'snackbar-loading');
    const token = await this._authService.getToken();
    const timeStamp = getFileNameTimeStamp('yyyyMMDDHHmmss').replace(
      /[\s+_]/g,
      '',
    );
    const zipFileName = `${fileId ? fileId + '_' : ''}${fileCategory}_${timeStamp}.zip`;

    if (this.zipWorker) {
      this.zipWorker.postMessage({ files, token });

      this.zipWorker.onmessage = ({ data }) => {
        const { zipBlob, hasFailedTxt, hasAllFailed } = data;

        if (zipBlob) {
          if (hasAllFailed) {
            this.showSnackbar(
              'All files failed to download. Please try to re-download the failed files. If you continue to have issues, please contact WCG Client Care as listed under Contact Us',
              'snackbar-error',
            );
            return;
          }
          this.saveAs(zipBlob, zipFileName);
          if (hasFailedTxt) {
            this.showSnackbar(
              `Some files failed to download. More info available inside zip file under 'Failed Downloads.txt' Please try to re-download the failed files. If you continue to have issues, please contact WCG Client Care as listed under Contact Us.`,
              'snackbar-warning',
            );
          } else {
            this.showSnackbar('Download complete', 'snackbar-success');
          }
        } else {
          this.showSnackbar(
            'All files failed to download. Please try to re-download the failed files. If you continue to have issues, please contact WCG Client Care as listed under Contact Us',
            'snackbar-error',
          );
        }
      };

      this.zipWorker.onerror = () => {
        this.showSnackbar(
          'All files failed to download. Please try to re-download the failed files. If you continue to have issues, please contact WCG Client Care as listed under Contact Us',
          'snackbar-error',
        );
      };
    }
  }

  saveAs(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  saveFile(data: any, fileName: string) {
    const blob = new Blob([data.body], { type: 'application/octet-stream' });
    const contentDisposition = data.headers.get('content-disposition');
    this.saveAs(
      blob,
      this.checkAndAppendFileExtension(fileName, contentDisposition),
    );
  }

  public checkAndAppendFileExtension = (
    filename: string,
    contentDisposition: string,
    appendFileName?: string,
  ): string => {
    const contentDispositionExtension = contentDisposition?.split('.').pop();

    if (
      contentDisposition?.indexOf('.') === -1 ||
      Boolean(contentDispositionExtension) === false
    ) {
      return filename;
    }

    const filenameExtension = filename.split('.').pop();
    let newFileName =
      contentDispositionExtension === filenameExtension
        ? filename
        : `${filename}.${contentDispositionExtension}`;

    if (appendFileName) {
      newFileName = `${newFileName.substring(
        0,
        newFileName.lastIndexOf('.'),
      )}${appendFileName}_.${contentDispositionExtension}`;
    }

    return newFileName;
  };

  deleteFile(documentId: string): Observable<any> {
    return this._http.delete(`${environment.irisApi}/document/${documentId}/`);
  }

  async deleteFiles(files: DeleteFiles[]): Promise<void> {
    if (files.length === 1) {
      await this.deleteSingleFile(files[0]);
    } else {
      await this.bulkDelete(files);
    }
    return Promise.resolve();
  }

  deleteFilesAsObservable(files: DeleteFiles[]): Observable<void> {
    return new Observable<void>((observer) => {
      (async () => {
        try {
          await this.deleteFiles(files);
          observer.next();
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();
    });
  }

  private async deleteSingleFile(file: DeleteFiles): Promise<void> {
    try {
      this.showSnackbar('Deleting document...', 'snackbar-loading');

      const response = await lastValueFrom(this.deleteFile(file.id));

      if (response) {
        this.showSnackbar('Document deleted', 'snackbar-loadsuccessing');
      } else {
        this.showSnackbar('Failed to delete document.', 'snackbar-error');
      }
    } catch {
      this.showSnackbar('Failed to delete document.', 'snackbar-error');
    }
  }

  private async bulkDelete(files: DeleteFiles[]): Promise<void> {
    if (!this.deleteWorker) {
      return;
    }

    try {
      const token = await this._authService.getToken();
      this.showSnackbar('Deleting documents...', 'snackbar-loading');
      const result = await this.postToWorker(this.deleteWorker, {
        files,
        token,
      });

      await this.handleBulkDeletionResult(result);
    } catch {
      this.showSnackbar('Failed to delete documents.', 'snackbar-error');
    }
  }

  private postToWorker(
    worker: Worker,
    payload: any,
  ): Promise<{ success: Array<unknown>; failed: Array<unknown> }> {
    return new Promise((resolve, reject) => {
      worker.onmessage = ({ data }) => resolve(data);
      worker.onerror = (error) => reject(error);
      worker.postMessage(payload);
    });
  }

  private async handleBulkDeletionResult(data: {
    success: Array<unknown>;
    failed: Array<unknown>;
  }): Promise<void> {
    if (data.failed.length > 0) {
      this.showSnackbar(
        `Deleted ${data.success.length} documents. Failed to delete ${data.failed.length}.`,
        'snackbar-warning',
      );
    } else {
      this.showSnackbar('Bulk documents deleted', 'snackbar-success');
    }
  }

  showSnackbar(message: string, messageClass: string): void {
    this._snackBar.open(message, 'Close', {
      panelClass: messageClass,
      duration: 5000,
    });
  }
}
